import React, { useContext, useEffect, useRef } from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import parse from 'html-react-parser';
import PrivacyPolicyPopup from '../../components/RegulatoryUpdateBundle/PrivacyPolicyPopup';

const PrivacyPolicyContainer = (): JSX.Element => {
  const { globalContext } = useContext(GlobalContext);
  const outsideClickCloseRef = useRef(null);
  const isLoggedIn = globalContext.customer.uuid;
  const [privacyPolicyPopup, setPrivacyPolicyPopup] = React.useState<boolean>(false);
  const serviceName = parse(globalContext.liquid.settings.service_name);
  const manageAccountDataClickHandler = (): void => {
    globalContext.gtm.pushVersionedEvent(
      '2.11',
      'CCPARequest',
      'ClickSubmitRequestLink',
      '',
      null,
      true
    );
    document.body.classList.add('is-reveal-open');
    setPrivacyPolicyPopup(true);
  };
  const outsideClickClosePopup = (e): void => {
    if (!outsideClickCloseRef?.current?.contains(e.target)) {
      setPrivacyPolicyPopup(false);
      document.body.classList.remove('is-reveal-open');
    }
  }
  useEffect(() => {
      if (globalContext.gb.utils.getFromStore('openManageAccountScreen') === 'true' && isLoggedIn) {
        setPrivacyPolicyPopup(true);
      }
      document.addEventListener('click', outsideClickClosePopup, true);
   }, []);

  return (
    <>
    <div
      data-testid="PrivacyPolicyContainer"
      id="privacy-policy-wrapper"
      className="row article-page responsive typography overrides page-padding-bottom pp">
      <div className="columns">
        <h1 className="heading-with-no-bottom-margin">Privacy Policy</h1>
        <p>
          <strong>Last Updated and Effective Date: </strong>
          {globalContext.liquid.settings.tenant_creation_date}
        </p>
        <div className="page-padding-bottom">
          <p>
            This Privacy Policy describes how CaaStle Inc. (d/b/a{' '}
            {serviceName}) (
            <strong>&quot;we,&quot; &quot;us,&quot;</strong> or{' '}
            <strong>&quot;our&quot;</strong>) collects, uses, shares, and
            protects personal information. This Privacy Policy covers our
            customer-facing operations in the United States and applies to the
            personal information that is (i) collected through our website
            located at {globalContext.liquid.settings.domain_name} and any other
            website or mobile application that we operate that links to this
            Privacy Policy (collectively, the <strong>&quot;Site&quot;</strong>)
            and (ii) collected in connection with your use of our products and
            services (collectively, including the Site, the{' '}
            <strong>&quot;Services&quot;</strong>). This Privacy Policy also
            includes information about your rights and choices regarding your
            personal information, and how you can reach us to get answers to
            your questions. You can skip to a particular topic by clicking on
            the headings listed below.
          </p>
          <p>
            This Privacy Policy does not apply to (i) information collected by
            third parties or websites or applications that we do not control,
            even if they are linked to or accessible through the Services; or
            (ii) information that we collect about any individual in their
            capacity as one of our current or former employees, job applicants,
            or professional business contacts. For information about our privacy
            practices with respect to information that we collect about
            employees, job applicants, and professional business contacts,
            please visit <a href="https://www.caastle.com/">caastle.com</a>.
          </p>
          <ul className="no-list">
            <li>
              <strong>
                <a href="#hwci">How We Collect Information.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#toiwc">Types of Information We Collect and Why.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#hwsi">How We Share Information.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#caott">Cookies and Other Tracking Technologies.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#yc">Your Choices.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#hwpi">How We Protect Information.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#hlwri">How Long We Retain Information.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#cp">Children&apos;s Privacy.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#oii">Other Important Information.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#ci">Contact Information.</a>
              </strong>
            </li>
            <li>
              <strong>
                <a href="#ifcr">Information for California Residents.</a>
              </strong>
            </li>
          </ul>
          <h4 id="hwci">
            <strong>
              I. <u>How We Collect Information.</u>
            </strong>
          </h4>
          <p>
            We collect personal information in a variety of different ways. The
            types of personal information that we obtain about you depends on
            how you interact with us and our Services. When we use the term
            &quot;personal information&quot; or refer to &quot;your
            information,&quot; we are referring to information that identifies,
            relates to, describes, or can be associated with, you. As explained
            in more detail below, we may collect personal information that is
            directly provided to us by you, obtained from a device associated
            with you or your household, or provided to us by a third-party
            source.
          </p>
          <ul className="list-dot">
            <li>
              <strong>Information Provided by You.</strong> We ask you to
              provide personal information when you create or update your
              account on the Site, log in to your account, sign up to receive
              communications and/or marketing materials from us, subscribe to or
              access our Services, make a purchase, contact customer service,
              provide feedback, and/or participate in our promotional
              activities.
              <br />
              <br />
              <strong>
                Information Collected from a Device Associated with You or Your
                Household.
              </strong>{' '}
              We and our third-party partners with whom we work to provide you
              Services may automatically collect certain of your information
              when you access or use the Services. Such information may include
              your Internet Protocol address (
              <strong>&quot;IP address&quot;</strong>), device identifier(s),
              browser type, and information about how you use and interact with
              the Services.
              <br />
              <br />
              We use tracking technologies, such as cookies, web beacons (also
              known as &quot;pixels&quot;), and other similar technologies
              (collectively, <strong>&quot;Tracking Technologies&quot;</strong>)
              to collect this information. A cookie is a small text file that is
              stored on a device for record-keeping or tracking purposes. Web
              beacons and pixels are pieces of code embedded in the Services
              that can collect information about a person&apos;s interaction
              with a particular web page or content, such as whether the person
              has clicked on a particular link. For more information about
              Tracking Technologies and how to manage your preferences, see{' '}
              <a href="#caott">
                Section IV (Cookies and Other Tracking Technologies)
              </a>{' '}
              and <a href="#yc">Section V (Your Choices)</a> below.
              <br />
              <br />
              Additionally, we use log files to record certain technical
              information about how our customers and Site visitors interact
              with the Services. This Site traffic information may include your
              IP address, browser type, internet service provider, timestamps,
              and other clickstream data. Our servers store this information in
              log files.
              <br />
              <br />
              If the information that we collect automatically identifies you or
              could be reasonably used to identify you, then it is treated as
              your personal information. In many cases, the information that we
              collect automatically is only used in de-identified or aggregated
              forms, and it does not identify any particular individual.
            </li>
            <li>
              <strong>Information Collected from Third-Party Sources.</strong>{' '}
              We may receive certain of your information from third-party
              sources, such as our Service Providers, Brand Partners, and
              Advertising Networks (each as defined in{' '}
              <a href="#cor">Section III.A (Categories of Recipients)</a> below)
              with whom we have partnered to operate and improve our Services.
              <br />
              <br />
              We may also obtain information about you from publicly available
              sources. If you interact with us on social media or use features,
              such as plug-ins, widgets, or other tools made available by social
              media platforms or networks in connection with our Services
              (including to login to our Site), we may collect information that
              you share with us through those mediums or information that social
              media platforms and networks share with us. This Privacy Policy
              does not apply to the collection of information by those third
              parties. For more information about the privacy practices of
              social media platforms and other third-party networks, please
              review the privacy policies and settings of the third-party
              platforms and networks that you use.
            </li>
          </ul>
          <h4 id="toiwc">
            <strong>
              II. <u>Types of Information We Collect and Why.</u>
            </strong>
          </h4>
          <p>
            The following provides examples of the types of information that we
            collect, identifies the sources from which we collect such
            information, and explains why we collect the information.
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Category of Information</th>
                <th>Examples of Information</th>
                <th>Sources of Collection</th>
                <th>Business Purpose(s)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Identifiers</td>
                <td>
                  Name, postal address, email address, phone number, unique
                  personal identifier, online identifier, IP address
                </td>
                <td>
                  We collect this information from you directly, collect it from
                  your device or browser, or receive it from third parties, such
                  as when we receive your name and email address from your
                  friends or relatives in connection with our Gift Membership
                  offerings.
                </td>
                <td>
                  We use this information to create your account on our Site, to
                  process your transactions and payments, to ship products to
                  you, to send you transaction information, to provide marketing
                  content and communications, to better understand our
                  customers, to provide customer service, to manage our Referral
                  Program and Gift Membership offerings, to run sweepstakes and
                  other promotional activities, to detect and prevent fraud or
                  other illegal activity, to collect overdue payments, for
                  security purposes, and for legal compliance purposes.
                </td>
              </tr>
              <tr>
                <td>Physical Characteristics</td>
                <td>Clothing size(s), clothing fit, and body shape</td>
                <td>
                  We collect this information from you directly, typically when
                  you subscribe to or purchase our Services, which may include
                  creating a size profile.
                </td>
                <td>
                  We use this information to process your transactions, to ship
                  products to you, to better understand our customers, to
                  improve our Services, to enhance your experiences with our
                  Services, and to provide customer service.
                </td>
              </tr>
              <tr>
                <td>Payment Information</td>
                <td>Credit/debit card number(s)</td>
                <td>
                  We collect this information from you directly when you
                  subscribe to or otherwise purchase our Services.
                </td>
                <td>
                  We use this information to process your transactions and
                  payments, to provide customer service, to detect and prevent
                  fraud or other illegal activity, to collect overdue payments,
                  for security purposes, and for legal compliance purposes.
                </td>
              </tr>
              <tr>
                <td>
                  Demographic Information and Certain Protected Characteristics{' '}
                </td>
                <td>
                  Gender information, age (general), familial status, household
                  information, estimated income
                </td>
                <td>
                  We collect this information from you directly, derive it from
                  your purchasing or browsing history, or receive it from third
                  parties.
                </td>
                <td>
                  We use this information to confirm that you meet the minimum
                  age requirement to use our Services, to improve our Services,
                  to provide marketing content and communications, to better
                  understand our customers, to conduct consumer research that
                  you consent to participate in, and for legal compliance
                  purposes.
                </td>
              </tr>
              <tr>
                <td>Commercial Information</td>
                <td>
                  Products and services rented, purchased, and/or considered,
                  and other purchasing and consumption history or tendencies
                </td>
                <td>
                  We collect this information from you directly, collect it from
                  your device or browser, or receive it from third parties.
                </td>
                <td>
                  We use this information to improve our Services, to provide
                  customer service, to fulfill orders and process transactions,
                  to provide marketing content and communications, to send
                  transactional communications, to conduct research and better
                  understand our customers, to detect and prevent fraud and
                  other illegal activity, for security purposes, and for legal
                  compliance purposes.
                </td>
              </tr>
              <tr>
                <td>Internet or Other Similar Network Activity</td>
                <td>
                  Browsing history, search history, and information about your
                  interactions with our Site, Services, emails, advertisements,
                  and other content
                </td>
                <td>
                  We collect this information from you directly, collect it from
                  your device or browser, or receive it from third parties.
                </td>
                <td>
                  We use this information to improve our Services, to enhance
                  our customers&apos; experiences with our Services, to send
                  transactional communications, to provide marketing content and
                  communications, to provide customer service, to conduct
                  research and better understand our customers, to detect and
                  prevent fraud and other illegal activity, for security
                  purposes, and for legal compliance purposes.
                </td>
              </tr>
              <tr>
                <td>Geolocation Data (Metro area)</td>
                <td>Physical location within a greater metropolitan area</td>
                <td>
                  We collect this information from you directly or from your
                  device or browser.
                </td>
                <td>
                  We use this information to improve our Services, to conduct
                  research and better understand our customers, to provide
                  marketing content and communications, to detect and prevent
                  fraud and other illegal activity, for security purposes, and
                  for legal compliance purposes.
                </td>
              </tr>
              <tr>
                <td>Sensory Data</td>
                <td>
                  Audio or visual recorded electronic information, olfactory
                  information, or similar information
                </td>
                <td>
                  We collect this information from you directly when you agree
                  to participate in consumer research surveys or provide us with
                  user-submitted content that includes this information, such as
                  product reviews and customer service feedback. We may also
                  obtain this information from publicly available sources, such
                  as social media platforms on which our Services or content are
                  featured.
                </td>
                <td>
                  We use this information to improve our Services and our
                  customers&apos; experiences with our Services, to conduct
                  research and better understand our customers, to provide
                  marketing content and communications, and to provide customer
                  service.
                </td>
              </tr>
              <tr>
                <td>Professional and/or Education-Related Information</td>
                <td>
                  Occupation, estimated income, and educational background or
                  history
                </td>
                <td>
                  We collect this information from you directly when you agree
                  to participate in consumer research surveys or provide us with
                  user-submitted content that includes this information. We also
                  may derive this information from your purchasing or browsing
                  history or receive it from third parties.
                </td>
                <td>
                  We use this information to improve our Services and our
                  customers&apos; experiences with our Services, to conduct
                  research and better understand our customers, to provide
                  marketing content and communications, and to provide customer
                  service.
                </td>
              </tr>
              <tr>
                <td>Inferences Drawn from Personal Information</td>
                <td>
                  Inferences drawn from or created based on any of the
                  information identified above
                </td>
                <td>
                  We derive this information from the information that we
                  collect from you directly, from your device or browser, or
                  from third parties.
                </td>
                <td>
                  We use this information to improve our Services and our
                  customers&apos; experience with our Services, to better
                  understand our customers, to provide customer service and
                  tailored recommendations, to provide marketing content and
                  communications, to prevent fraud and other illegal activity,
                  for security purposes, and for legal compliance purposes.
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            Under certain U.S. state privacy laws, payment information, such as
            a credit/debit card number together with any required security code,
            and account login information, such as a username and password, are
            considered &quot;sensitive personal information.&quot; We only
            collect payment information and login credentials for accounts you
            have with us, when you provide them to us. We only use this
            information to provide you Services, to maintain the security of our
            Services and your information, or as we may otherwise expressly
            disclose to you at the time we collect the information. We do not
            use or disclose any sensitive personal information to draw
            inferences or build a profile about you or to conduct advertising of
            any kind. We do not &quot;sell&quot; or &quot;share&quot; for
            advertising purposes of any kind any personal information that is
            defined as &quot;sensitive personal information&quot; under
            applicable U.S. state privacy law(s).
          </p>
          <p>
            We do not collect or use any personal information for purposes of
            profiling consumers in furtherance of any decisions that produce
            legal or similarly significant effects, such as decisions regarding
            access to financial or lending services, housing, educational or
            employment opportunities, criminal justice, healthcare, or any other
            similarly essential goods or resources.
          </p>
          <p>
            We may use aggregate, statistical and &quot;de-identified&quot;
            information, which is anonymized and does not specifically identify
            you or any other individual person(s). We only use such information
            in a de-identified form and will not attempt to reidentify such
            information, except as permitted or required by applicable law.
          </p>

          <h4 id="hwsi">
            <strong>
              III. <u>How We Share Information.</u>
            </strong>
          </h4>
          <p>
            We may disclose your information to our affiliates and/or to certain
            third parties as described below.
          </p>
          <p id="cor">
            <strong>A. Categories of Recipients</strong>
          </p>
          <p>
            We may share information with the following categories of recipients
            for the business purposes that are identified in{' '}
            <a href="#toiwc">Section II</a> and{' '}
            <a href="#wwdi">Section III.B</a>:{' '}
          </p>
          <ul className="list-dot">
            <li>
              <strong>
                Our Affiliates and in Connection with Business Transactions.
              </strong>{' '}
              We may share information with other companies within the CaaStle
              corporate family (e.g., sister companies, subsidiaries, joint
              ventures, or other companies under common control) (our{' '}
              <strong>&quot;Affiliates&quot;</strong>). If another person or
              entity acquires, or plans to acquire, our company, business, or
              assets, we may share information with that person or entity,
              including at the negotiation stage. Some or all of the personal
              information that we collect may be part of the assets that are
              transferred to complete this type of transaction.
            </li>
            <li>
              <strong>Service Providers.</strong> We share information with
              service providers that we have engaged to assist us with providing
              and improving the Services and our business operations (
              <strong>&quot;Service Providers&quot;</strong>). For example, we
              use Service Providers to assist with managing our Site, processing
              payments, fulfilling and shipping orders, providing technical
              support, conducting surveys and research, analytics, marketing our
              Services, communicating with our customers, detecting and
              preventing fraud, collecting overdue payments, dispute resolution,
              and legal compliance. We share information with Service Providers
              only as necessary for them to perform their functions.
            </li>
            <li>
              <strong>Brand Partners.</strong> We may partner with other
              parties, such as fashion companies and retailers, media companies,
              or digital creators, to jointly offer products or services to our
              customers (<strong>&quot;Brand Partners&quot;</strong>). We may
              share information with a Brand Partner for their direct marketing
              purposes, if you express interest in our jointly offered products
              and services. Our Brand Partners may use your information to
              provide marketing content and promotional offers about other
              products and services that may be of interest to you. Please note
              that we do not share text messaging opt-in data with third parties
              for their marketing purposes.
            </li>
            <li>
              <strong>Advertising Networks.</strong> We partner with advertising
              networks to provide advertisements that promote the Services on
              third-party websites and applications that may be of interest to
              you (<strong>&quot;Advertising Networks&quot;</strong>). We may
              share your information with Advertising Networks to provide
              tailored advertising content. Additionally, these Advertising
              Networks may collect information about your visits to our Site
              using Tracking Technologies. Please see{' '}
              <a href="#yc">Section V (Your Choices)</a> below for information
              about managing your preferences in relation to targeted
              advertising.
            </li>
            <li>
              <strong>Promotion Partners.</strong> We may partner with other
              parties to offer or co-sponsor contests, sweepstakes, giveaways,
              or other promotions (
              <strong>&quot;Promotion Partners&quot;</strong>). If you decide to
              participate in one of these promotions, the information that you
              provide to participate may be shared with us and with the relevant
              Promotion Partner.
            </li>
            <li>
              <strong>Gift Recipients.</strong> If you purchase a Gift
              Membership, we will email your designated gift recipient (a{' '}
              <strong>&quot;Gift Recipient&quot;</strong>) to notify them that
              they have received a gift from you. We will share your name with
              the gift recipient, using the name that you provide us when you
              purchase the Gift Membership.
            </li>
            <li>
              <strong>Governmental Authorities and Legal Process.</strong> We
              may disclose information to federal, state, and/or local
              governmental agencies and authorities, including those related to
              law enforcement (
              <strong>&quot;Governmental Authorities&quot;</strong>), if we are
              required to do so to comply with the law or legal processes. When
              permitted, we may disclose personal information to Governmental
              Authorities in response to their request(s) or in connection with
              a legal process when we believe that disclosing such information
              is necessary or advisable to defend against a legal claim or to
              protect our or others&apos; safety, rights, or property.
            </li>
            <li>
              <strong>Public.</strong> Our Services may provide the opportunity
              to post reviews, photos, or comments in a public forum, such as
              our Site or on our social media profile pages. If you decide to
              submit this type of information through these channels, that
              information may be publicly available.
            </li>
            <li>
              <strong>Other Third Parties.</strong> We may disclose your
              information to other third parties when we have your consent or
              direction to do so.
            </li>
          </ul>
          <p className="pb-5" id="wwdi">
            <strong>B. Why We Disclose Information</strong>
          </p>
          <p>
            The table below indicates the categories of personal information
            that we may disclose in a variety of contexts. Please note that,
            because this list is comprehensive, it may refer to types of
            information that we collect and share about other people, and not
            about you. The types of information that we collect and share about
            you will depend on how you interact with us and the Services. For
            example, we do not collect or share employment or education
            information about our customers who simply interact with us by
            accessing or purchasing our Services. If you agree to participate in
            a consumer research survey or feedback interview with us, however,
            we may collect and share information about your employment or
            education history with a Service Provider that helps us conduct
            consumer research and data analytics.
          </p>
          <p>
            We do not receive money in exchange for sharing your personal
            information, and we have not sold personal information in exchange
            for money in the past twelve (12) months. However, like many other
            companies in the e-commerce and retail space, we share certain
            personal information with third parties with whom we have partnered
            to provide, improve, and/or advertise our Services. In certain
            instances, if you express interest in the products and services that
            we jointly offer with our Brand Partners, we may share some of your
            personal information with those Brand Partners for their direct
            marketing purposes. Under certain U.S. state law(s), these
            disclosures may be considered a &quot;sale&quot; or
            &quot;sharing&quot; for cross-context behavioral advertising
            purposes (also referred to as &quot;targeted advertising&quot;). The
            table below provides a list of the categories of information that we
            may disclose or that we have disclosed in the past twelve (12)
            months that may be considered a &quot;sale&quot; or
            &quot;sharing&quot; for cross-context behavioral advertising
            purposes under applicable U.S. state privacy laws(s). Please note,
            we do not knowingly sell or share the personal information of
            persons under sixteen (16) years of age.
          </p>
          <table className="table three-column-table">
            <thead>
              <tr>
                <th>Category of Information</th>
                <th>
                  Categories of Recipients to Whom We Disclose Personal
                  Information for Business Purpose(s)
                </th>
                <th>
                  Categories of Recipients with Whom We Share Personal
                  Information for Valuable Consideration or for Cross-Context
                  Behavioral Advertising Purposes
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Identifiers</td>
                <td>
                  <ul className="list-dot">
                    <li>Our Affiliates</li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>Advertising Networks</li>
                    <li>
                      {' '}
                      Promotion Partner(s), if you have provided your
                      information in connection with a promotion that we are
                      co-sponsoring with the Promotion Partner(s){' '}
                    </li>
                    <li>
                      Gift Recipients for whom you have purchased a Gift
                      Membership, only as necessary to identify the gift
                      purchaser by the name provided
                    </li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>Advertising Networks</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Physical Characteristics</td>
                <td>
                  <ul className="list-dot">
                    <li>Our Affiliates</li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>Not Applicable</td>
              </tr>
              <tr>
                <td>Payment Information</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>Not Applicable</td>
              </tr>
              <tr>
                <td>
                  Demographic Information and Certain Protected Characteristics
                </td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>Not Applicable</td>
              </tr>
              <tr>
                <td>Commercial Information</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>Advertising Networks</li>
                    <li>Promotion Partners</li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>Advertising Networks</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Internet or Other Similar Network Activity</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>Advertising Networks</li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Brand Partners whose branded products, services, or
                      content are featured or accessible through the Services
                    </li>
                    <li>Advertising Networks</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Geolocation Data (Metro Area)</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>Advertising Networks</li>
                    <li>
                      Governmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="list-dot">
                    <li>Advertising Networks</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Sensory Data</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>Brand Partners</li>
                    <li>
                      Public, if you share such information in a public forum,
                      such as publishing a product review on our Site, or if you
                      otherwise provide consent to making such content publicly
                      available
                    </li>
                  </ul>
                </td>
                <td>Not Applicable</td>
              </tr>
              <tr>
                <td>Professional and/or Education-Related Information</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                  </ul>
                </td>
                <td>Not Applicable</td>
              </tr>
              <tr>
                <td>Inferences Drawn from Personal Information</td>
                <td>
                  <ul className="list-dot">
                    <li>
                      Our Affiliates with whom we work to achieve the identified
                      purposes
                    </li>
                    <li>
                      Service Providers that provide us with services related to
                      the business purposes identified in Section II above
                    </li>
                    <li>Brand Partners</li>
                    <li>Advertising Networks</li>
                    <li>
                      AdGovernmental Authorities and in connection with legal
                      processes, only as necessary or appropriate to comply with
                      applicable law or address potentially unlawful activity
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="list-dot">
                    <li>Advertising Networks</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
          <h4 id="caott">
            <strong>
              IV. <u>Cookies and Other Tracking Technologies.</u>
            </strong>
          </h4>
          <p>
            We and certain of our third-party partners use cookies and other
            Tracking Technologies to gather information when you interact with
            our Site, mobile applications, and/or email communications. Some
            Tracking Technologies help us maintain the security of our Site and
            your account, prevent crashes, fix bugs, save certain of your
            preferences, and assist with basic site functions. These Tracking
            Technologies are referred to as &quot;strictly necessary&quot;
            because they are necessary for our Services to work properly. We use
            &quot;performance&quot; cookies and other Tracking Technologies to
            assess the performance of our Services, for data analytics purposes,
            and to help us understand how our customers and Site visitors
            interact with the Services. We use &quot;functional&quot; cookies
            and other Tracking Technologies to personalize your experience with
            our Services and offer enhanced functionality when you access or use
            the Services. For example, we use &quot;functional&quot; Tracking
            Technologies to maintain your preferences over time and recognize
            you when you return to the Services.{' '}
          </p>
          <p>
            We also permit certain of our third-party partners to use
            &quot;advertising&quot; cookies and other Tracking Technologies on
            our Site for analytics and advertising purposes, including to help
            manage and display advertisements, to tailor content to your
            interests, and/or to send targeted communications, such as abandoned
            shopping cart reminders. Our third-party partners may use
            information accessible through these Tracking Technologies to
            provide advertising about products and services tailored to your
            interests, and such advertisements may appear on our websites or
            other, third-party websites that we do not control. These third
            parties may also collect information over time and across other
            websites, in addition to our Site. Under certain privacy laws, this
            may be considered &quot;targeted,&quot; &quot;cross-context
            behavioral,&quot; or &quot;interest-based&quot; advertising. You may
            opt-out of these &quot;advertising&quot; Tracking Technologies by
            clicking the &quot;
            <a className="ot-sdk-show-settings" id="dnss">
              Do Not Sell or Share My Personal Information
            </a>
            &quot; link in the footer of our Site&apos;s homepage and following
            the instructions to set your preferences. Please see{' '}
            <a href="#yc">Section V (Your Choices)</a> and{' '}
            <a href="#ci">Section XI (California residents)</a> below for more
            information about your options.
          </p>
          <p>
            You may also block or delete cookies from your device by modifying
            your browser settings. Please note that the Services may not
            function properly for you if you block or delete certain cookies. To
            find out more information about cookies generally, including
            information about how to delete cookies, please visit{' '}
            <a href="https://allaboutcookies.org/">
              https://allaboutcookies.org/
            </a>
            .
          </p>
          <h4 id="yc">
            <strong>
              V. <u>Your Choices.</u>
            </strong>
          </h4>
          <p>
            As permitted or required under applicable law, you may exercise the
            rights described in this section. Please note that some of your
            rights may vary depending on your country, province, or state of
            residence.
          </p>
          <ul className="list-dot">
            <li>
              <strong>Access to Your Personal Information.</strong> You may
              request us to confirm whether and how we have processed your
              personal information. If required by law, upon request, we will
              grant you access to the personal information that we have about
              you. In some cases, you may have the right to request that we
              provide you a copy of your personal information in a portable
              format. Please note that we will deny a request to access personal
              information if we are unable to verify that it is you or someone
              legally authorized to act on your behalf who is making the
              request. You may submit a request to access your personal
              information by doing one of the following:
              <ol className='list-numeric'>
                <li>
                  Clicking the{' '}
                  &quot;<a onClick={manageAccountDataClickHandler}id="manageAccountData">CLICK HERE TO SUBMIT A REQUEST
                  </a>&quot;{' '}
                  link at the bottom of this Privacy Policy and completing the
                  online request form; or
                </li>
                <li>
                  Contacting us by email at{' '}
                  <a
                    href={
                      'mailto:' + globalContext.liquid.settings.contact_email
                    }>
                    {globalContext.liquid.settings.contact_email}
                  </a>{' '}
                  or by phone at{' '}
                  <a
                    href={
                      'tel:' +
                      globalContext.liquid.settings.contact_phone_number_form
                    }>
                    {
                      globalContext.liquid.settings
                        .contact_phone_alternative_form
                    }
                  </a>
                  .
                </li>
              </ol>
            </li>
            <li>
              <strong>Changes to Your Personal Information.</strong> In most
              cases, we rely on you to update and correct your personal
              information. Most of our websites allow you to modify your account
              profile. If our Site does not permit you to update or correct
              certain information, you may contact us by email at{' '}
              <a href={'mailto:' + globalContext.liquid.settings.contact_email}>
                {globalContext.liquid.settings.contact_email}{' '}
              </a>
              or by phone at{' '}
              <a
                href={
                  'tel:' +
                  globalContext.liquid.settings.contact_phone_number_form
                }>
                {' '}
                {
                  globalContext.liquid.settings.contact_phone_alternative_form
                }{' '}
              </a>{' '}
              to request that your information be modified.
            </li>
            <li>
              <strong>Delete Your Personal Information.</strong> You may request
              information about how long we keep a specific type of information,
              or request that we delete your personal information. If required
              by law, we will grant a request to delete information, but you
              should note that, in many situations, we may deny all or a portion
              of the request because we need to keep your personal information
              on file to continue providing you with Services, comply with our
              legal obligations, resolve disputes, enforce our agreements,
              protect against fraudulent or other unlawful activity, or for
              another one of our business purposes. We may also deny the request
              if we are unable to verify that you or someone legally authorized
              to act on your behalf is making the request. You may submit a
              request to delete your personal information by doing one of the
              following:
              <ol className='list-numeric'>
                <li>
                  Clicking the{' '}
                  &quot;<a
                    onClick={manageAccountDataClickHandler}
                    id="manageAccountData">
                    CLICK HERE TO SUBMIT A REQUEST
                  </a>&quot;{' '}
                  link at the bottom of this Privacy Policy and completing the
                  online request form; or
                </li>
                <li>
                  Contacting us by email at{' '}
                  <a
                    href={
                      'mailto:' + globalContext.liquid.settings.contact_email
                    }>
                    {globalContext.liquid.settings.contact_email}{' '}
                  </a>{' '}
                  or by phone at{' '}
                  <a
                    href={
                      'tel:' +
                      globalContext.liquid.settings.contact_phone_number_form
                    }>
                    {
                      globalContext.liquid.settings
                        .contact_phone_alternative_form
                    }
                  </a>
                  .
                </li>
              </ol>
            </li>
            <li>
              <strong>
                Opt-Out of &quot;Sales&quot; and/or Processing Your Personal
                Information for Targeted Advertising Purposes.
              </strong>{' '}
              As described in{' '}
              <a href="#caott">
                Section IV (Cookies and Other Tracking Technologies)
              </a>{' '}
              above, our use of &quot;advertising&quot; Tracking Technologies
              may be considered a &quot;sale&quot; or &quot;sharing&quot; of
              personal information for targeted advertising purposes under
              applicable law. You may opt out of these Tracking Technologies by
              doing one of the following:{' '}
              <ol className='list-numeric'>
                <li>
                  Accessing the privacy preference center and setting your
                  preferences via this link:{' '}
                  <a className="ot-sdk-show-settings" id="dnss">
                    Do Not Sell or Share My Personal Information
                  </a>
                  , which is also accessible via the footer of our Site&apos;s
                  homepage. Please note that, in some cases, your opt-out
                  preferences may only be linked to your device or browser
                  identifier. If you visit our Site from another device or using
                  another browser (or after your original browser&apos;s cookies
                  have been erased), you may need to renew your opt-out choice;
                </li>
                <li>
                  Visiting our Site and broadcasting a legally-recognized
                  opt-out preference signal, such as the Global Privacy Control
                  (GPC) on the browser(s) and/or browser extension(s) that you
                  use to access our Site, to the extent that such browser(s)
                  and/or browser extension(s) support the signal you are using.
                  Please note that, in some cases, your opt-out preference
                  signal may only be linked to your device or browser
                  identifier. If you visit our Site from another device or using
                  another browser (or after your original browser&apos;s cookies
                  have been erased), you may need to renew your opt-out choice.
                  For more information about how to use the GPC browser signal,
                  please visit{' '}
                  <a href="https://globalprivacycontrol.org/">
                    {' '}
                    https://globalprivacycontrol.org/
                  </a>
                  ; or
                </li>
                <li>
                  Emailing us at{' '}
                  <a
                    href={
                      'mailto:' + globalContext.liquid.settings.contact_email
                    }>
                    {globalContext.liquid.settings.contact_email}
                  </a>{' '}
                  with your opt-out request.
                </li>
              </ol>
              As described in further detail in{' '}
              <a href="#hwsi">Section III (How We Share Information)</a> above,
              we may also share certain personal information with our
              third-party partners for direct or targeted advertising purposes,
              using means other than advertising Tracking Technologies. Under
              applicable law, disclosing information for these purposes may be
              considered a &quot;sale&quot; or &quot;sharing&quot; of personal
              information for targeted advertising purposes. You may opt out of
              having your information &quot;sold&quot; or &quot;shared&quot; for
              these purposes, by means other than Tracking Technologies, by
              doing one of the following:
              <ol className='list-numeric'>
                <li>
                  Submitting an opt-out request using this link:{' '}
                  <a className="ot-sdk-show-settings">Opt-Out Request Form</a>,
                  which is also linked within the privacy preference center that
                  is accessible via the &quot;
                  <a className="ot-sdk-show-settings" id="dnss">
                    Do Not Sell or Share My Personal Information
                  </a>
                  &quot; link in the footer of our Site&apos;s homepage; or
                </li>
                <li>
                  Emailing us at{' '}
                  <a
                    href={
                      'mailto:' + globalContext.liquid.settings.contact_email
                    }>
                    {globalContext.liquid.settings.contact_email}
                  </a>{' '}
                  with your opt-out request.
                </li>
              </ol>
            </li>
            <li>
              <strong>&quot;Do Not Track.&quot;</strong> &quot;Do Not
              Track&quot; is a privacy preference that users can set in certain
              web browsers. Our Services are not, at this time, configured to
              recognize or respond to &quot;Do Not Track&quot; signals
              transmitted by web browsers.{' '}
            </li>
            <li>
              <strong>Promotional Emails.</strong> You can stop receiving
              promotional emails by following the unsubscribe instructions in
              the e-mails that you receive. If you decide not to receive
              promotional emails, we may still send you service and
              transaction-related communications.
            </li>
            <li>
              <strong>Promotional Text Messages.</strong> If you receive a text
              message from us that contains promotional information, you can
              opt-out of receiving future text messages by replying “STOP.” We
              do not share text messaging opt-in data with third parties for
              their marketing purposes.
            </li>
            <li>
              <strong>Push Notifications.</strong> We may send you push
              notifications through our mobile applications. You may opt out
              from receiving these types of communications by changing the
              settings on your mobile device. We may also collect location-based
              information if you use our mobile applications. If applicable, you
              may opt out of this collection by changing the settings on your
              mobile device.
            </li>
            <li>
              <strong>Revoke Consent.</strong> If you revoke your consent for
              the processing of personal information, we may no longer be able
              to provide you Services. In some cases, we may limit or deny your
              request to revoke consent if the law permits or requires us to do
              so, or if we are unable to verify your identity. You may revoke
              consent to processing (where such processing is based upon
              consent) by contacting us as described below.
            </li>
          </ul>
          <p>
            Please note that, in many instances, we are only obligated to
            respond to verifiable consumer requests. And, in some of those
            instances, we are required by law to verify that the request is
            actually being made by you or by an agent who is legally authorized
            to act on your behalf, before we can grant the request.
          </p>
          <p>
            To verify a request, we may ask you to confirm your identity by
            logging into your {serviceName}{' '}
            account (if applicable), or to provide information that we can use
            to confirm your identity, such as your name, email address, the
            last item you purchased or rented from us, and/or other verifiable
            information. We also may implement other authentication procedures
            that are appropriate based on the nature of the request. If we
            collect any new personal information (that we have not already
            collected for another purpose) to verify a request, we will only use
            that information to verify the request and to comply with any legal
            obligations we have to retain a record of the request. Even if we
            are able to verify a request, we may nevertheless deny the request
            because we are either required or permitted to by law. For example,
            if you have a subscription with us, we are permitted to deny a
            request to delete your personal information because we need to keep
            the information on file to continue providing you Services. In some
            instances, you may have the right to appeal our decision to deny a
            privacy rights request. To appeal a decision to deny a request,
            please contact us at{' '}
            <a href={'mailto:' + globalContext.liquid.settings.contact_email}>
              {globalContext.liquid.settings.contact_email}
            </a>{' '}
            or at{' '}
            <a
              href={
                'tel:' + globalContext.liquid.settings.contact_phone_number_form
              }>
              {globalContext.liquid.settings.contact_phone_alternative_form}
            </a>{' '}
            and reference the nature, format, and date of your original request
            and describe the basis for your appeal and any other details that
            are relevant to our review of your appeal.
          </p>
          <p>
            To designate an authorized agent to make requests on your behalf,
            please contact us (or have your authorized agent contact us) using
            one of the available methods for submitting requests as described
            above in this <a href="#yc">Section V</a>. If we receive a request
            from your agent, we may reach out to you directly to confirm their
            authority to make the request on your behalf.
          </p>
          <p>
            We do not discriminate against individuals who exercise any of their
            privacy rights under applicable law.
          </p>
          <h4 id="hwpi">
            <strong>
              VI. <u>How We Protect Personal Information.</u>
            </strong>
          </h4>
          <p>
            We maintain reasonable and appropriate physical, electronic, and
            procedural safeguards that are designed to help protect your
            personal information from unauthorized access, use, and disclosure.
            Although we work diligently to protect the personal information that
            is in our possession, no method of transmission over the internet or
            security system is 100% secure, and we cannot guarantee that our
            security measures will prevent all unauthorized persons from
            illegally accessing personal information. In the event that we are
            required by law to inform you of a breach of your personal
            information, we may notify you electronically, in writing, or by
            telephone, if permitted to do so by law.
          </p>
          <p>
            If our Site permits you to create an account, you will be prompted
            to create a password and/or to login using credentials from a
            third-party network or social media platform, such as Google or
            Facebook. You are responsible for maintaining the confidentiality of
            your password and login credentials, and you are responsible for any
            access to or use of your account by someone else, whether or not
            such access or use has been authorized by you. You should notify us
            of any actual or suspected unauthorized use of your password or
            account.
          </p>
          <h4 id="hlwri">
            <strong>
              VII. <u>How Long We Retain Information.</u>
            </strong>
          </h4>
          <p>
            We retain personal information for the period necessary to fulfill
            the purposes outlined in this Privacy Policy, including for the
            purposes of satisfying any legal, accounting, or other reporting
            requirements, unless a longer retention period is required or
            permitted by applicable law. To determine the appropriate retention
            period for personal information, we consider the amount, nature, and
            sensitivity of the information, the potential risk of harm from
            unauthorized use or disclosure of the information, the purposes for
            which we obtained the information and whether we can achieve those
            purposes through other means, and applicable legal requirements.
          </p>
          <h4 id="cp">
            <strong>
              VIII. <u>Children&apos;s Privacy.</u>
            </strong>
          </h4>
          <p>
            Our Services are not intended for or directed toward children under
            the age of 13. If we become aware that a child under the age of 13
            has provided us with personal information, we will delete the
            information from our records. If you are the parent or legal
            guardian of a child whose information you believe has been provided
            to us without your consent, please contact us at{' '}
            <a href={'mailto:' + globalContext.liquid.settings.contact_email}>
              {globalContext.liquid.settings.contact_email}
            </a>
            .
          </p>
          <h4 id="oii">
            <strong>
              IX. <u>Other Important Information</u>
            </strong>
          </h4>
          <p>
            The following additional information relates to our privacy
            practices:
          </p>
          <ul className="list-dot">
            <li>
              <strong>Transmission of Information to Other Countries.</strong>{' '}
              CaaStle Inc. is located in the United States. The Services are
              hosted in the United States and are intended for and directed to
              consumers in the United States. If you do not reside in the United
              States and submit personal information to us, your information may
              be processed in a country where privacy laws may be less stringent
              than the laws in your country of residence. Nonetheless, where
              possible, we take steps to treat personal information using the
              same privacy principles that apply under the law of the country in
              which we first received the information. By submitting your
              personal information to us, you agree to the transfer, storage,
              and processing of your information in a country other than your
              country of residence, including, but not necessarily limited to,
              the United States.
            </li>
            <li>
              <strong>Third Party Applications/Websites.</strong> We have no
              control over the privacy practices of websites or applications
              that we do not own or control. In the event that we provide links
              to other websites or applications that we do not control, whether
              or not those other websites or applications refer to our products,
              Services, or branding, this Privacy Policy does not apply to those
              other websites or applications. Visitors to those websites and
              applications are advised to read the policies on those websites
              and applications.
            </li>
            <li>
              <strong>Changes to This Privacy Policy.</strong> We may modify
              this Privacy Policy from time to time. The most recent version of
              the Privacy Policy will always be posted on the Site, with the
              effective date posted at the top of the Privacy Policy. If we make
              changes that materially affect your privacy rights, we will use
              reasonable efforts to notify you of such changes. By visiting the
              Site or using the Services after the effective date, you are
              deemed to have notice of our then-current Privacy Policy.
            </li>
          </ul>
          <h4 id="ci">
            <strong>
              X. <u>Contact Information.</u>
            </strong>
          </h4>
          <p>
            If you have any questions, comments, or concerns about our privacy
            practices, please contact us by one of the following methods:
          </p>
          <p>
            By e-mail, at{' '}
            <a href={'mailto:' + globalContext.liquid.settings.contact_email}>
              {globalContext.liquid.settings.contact_email}
            </a>
            {globalContext.liquid.settings.contact_phone !== '' && (
              <>
                {' '}
                , <br />
                By phone, at{' '}
                <a
                  href={
                    'tel:' +
                    globalContext.liquid.settings.contact_phone_number_form
                  }>
                  {globalContext.liquid.settings.contact_phone_alternative_form}
                </a>
                ,
              </>
            )}{' '}
            or <br />
            By writing to us at the following address:
          </p>
          <blockquote>
            CaaStle Inc. <br />
            5 Penn Plaza, Fl. 4<br />
            New York, NY 10001
            <br />
            Attn: Legal Department
          </blockquote>
          <h4 id="ifcr">
            <strong>
              XI. <u>Information for California Residents.</u>
            </strong>
          </h4>
          <p>
            California law requires us to disclose the following information
            with respect to our privacy practices. If you are a California
            resident, this section applies to you, in addition to the rest of
            this Privacy Policy.{' '}
          </p>
          <ul className="list-dot">
            <li>
              <strong>California Shine the Light Law.</strong> California
              residents may request information about the categories of personal
              information (if any) that we share with third parties for those
              parties&apos; direct marketing purposes. To do so, please contact
              us by email at{' '}
              <a href={'mailto:' + globalContext.liquid.settings.contact_email}>
                {' '}
                {globalContext.liquid.settings.contact_email}
              </a>{' '}
              or by phone at{' '}
              <a
                href={
                  'tel:' +
                  globalContext.liquid.settings.contact_phone_number_form
                }>
                {globalContext.liquid.settings.contact_phone_alternative_form}
              </a>
              .
            </li>
            <li>
              <strong>
                Categories of Personal Information We Collect, Sources of
                Collection, and Purposes for Collection.
              </strong>{' '}
              Please see{' '}
              <a href="#hwci">Section I (How We Collect Information)</a> and{' '}
              <a href="#toiwc">
                Section II (Types of Information We Collect and Why)
              </a>{' '}
              of this Privacy Policy for a list of the categories of personal
              information that we collect, the categories of sources from which
              we collect personal information, and our business purposes for the
              collection and use of personal information.
            </li>
            <li>
              <strong>
                Categories of Personal Information Disclosed, Categories of
                Recipients, and Purposes for Disclosure.
              </strong>{' '}
              Please see{' '}
              <a href="#hwsi">Section III (How We Share Information)</a> of this
              Privacy Policy for a list of the categories of personal
              information that we may disclose or that we have disclosed to
              third parties in the past twelve (12) months for our business
              purposes and the categories of recipients.
            </li>
            <li>
              <strong>
                &quot;Sale&quot; and/or &quot;Sharing&quot; of Personal
                Information.
              </strong>{' '}
              Under California&apos;s privacy law(s), &quot;sale&quot; is
              broadly defined as the transfer of personal information to a third
              party for valuable consideration (even if there is no exchange of
              money), which includes many disclosures of information that go
              beyond the ordinary concept of a sale. Please see{' '}
              <a href="#hwsi">Section III (How We Share Information)</a> of this
              Privacy Policy for a list of the categories of personal
              information that we may disclose or that we have disclosed to
              third parties in the past twelve (12) months that may be
              considered a &quot;sale&quot; or &quot;sharing&quot; for
              cross-context behavioral advertising purposes under
              California&apos;s privacy law(s). Please see{' '}
              <a href="#yc">Section V (Your Choices)</a> above for information
              about how you can exercise your right to opt out of the
              &quot;sale&quot; and &quot;sharing&quot; of your information.
            </li>
            <li>
              <strong>Right to Access.</strong> Subject to certain limitations,
              and in addition to the choices described in Section V of this
              Privacy Policy, a California resident has the right to request
              access to the specific pieces of personal information that we
              collect about them and the right to know the categories of
              personal information that we collect, disclose, &quot;sell&quot;
              or &quot;share,&quot; our business purpose(s) for doing the
              foregoing, the categories of recipients to whom or with whom we
              have disclosed, &quot;sold,&quot; or &quot;shared&quot; personal
              information, and the categories of sources from which we have
              collected personal information. Please note that we are required
              by law to verify that a request to access your personal
              information is being made by you or by an agent who is legally
              authorized to act on your behalf. Please see{' '}
              <a href="#yc">Section V (Your Choices)</a> above for more
              information about how to exercise your right to access your
              personal information. Please note that we are only required to
              respond to an individual consumer&apos;s request for access twice
              within a twelve (12)-month period.
            </li>
            <li>
              <strong>Right to Delete Your Personal Information.</strong> A
              California resident has the right to request that we delete the
              personal information that we have collected about them. Please see{' '}
              <a href="#yc">Section V (Your Choices)</a> above for more
              information about how to exercise your right to request the
              deletion of your personal information and for information about
              how we verify your identity.
            </li>
            <li>
              <strong>
                Right to Limit the Use of Your Sensitive Personal Information.
              </strong> Under certain circumstances, California residents have the right
              to limit the use of their &quot;sensitive personal
              information,&quot; as that term is defined by applicable
              California law. We do not use or disclose sensitive personal
              information for any purpose that gives rise to a right to limit
              the use of sensitive personal information under applicable
              California law. We only use sensitive personal information to
              provide you Services, to maintain the security of our Services and
              your information, or as we may otherwise expressly disclose to you
              at the time we collect the information. We do not use or disclose
              any sensitive personal information to infer characteristics about
              you or to conduct advertising of any kind.
            </li>
            <li>
              <strong>Notice of Financial Incentive.</strong> Our Referral
              Program provides participating customers with certain perks, such as referral
              credits and promotional offers. We also may conduct other
              promotional activities, such as sponsoring sweepstakes or
              contests. When you participate in our Referral Program or one of
              our promotional campaigns, we may ask you to provide certain
              personal information, such as your name and contact information.
              Because these programs involve the collection of personal
              information, they may be interpreted as a &quot;financial
              incentive&quot; program under California law. We do not assign a
              monetary value to the information we collect. Based on our
              reasonable estimate, the value of personal information that you
              provide to us is related to the value of the free or discounted
              product, services, or other benefits that we may provide through
              the applicable program, less the expense related to offering those
              products, services, and other benefits to program participants.
              You may withdraw from participating in our Referral Program and
              any other promotional campaigns at any time by contacting us at
              the designated method set forth in the program&apos;s rules or in
              our{' '}
              <a href="/pages/terms-of-service" target="_blank">
                Terms of Service
              </a>.
            </li>
          </ul>
          <p>
            <a onClick={manageAccountDataClickHandler} id="manageAccountData">
              CLICK HERE TO SUBMIT A REQUEST.
            </a>
          </p>
        </div>
      </div>
    </div>
    {privacyPolicyPopup && <div className='reveal-overlay' style={{ display: 'block' }}>
        <div data-reveal className="reveal privacy-policy-ccpa-popup regulatory-updates-modal" style={{ display: 'block' }} ref={outsideClickCloseRef} >
         <PrivacyPolicyPopup setClosePopup={setPrivacyPolicyPopup} />
        </div>
      </div>}
    </>
  );
};

export default PrivacyPolicyContainer;
